import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Plus, Trash2, Save, ArrowLeft, Search, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateQuestionnairePage = () => {
  const navigate = useNavigate();
  const { questionnaireId } = useParams();
  const isEditMode = !!questionnaireId;

  const [loading, setLoading] = useState(false);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [purpose, setPurpose] = useState('conventional');
  const [sequences, setSequences] = useState([
    { sequence_number: 1, question_ids: [], collapsed: false }
  ]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pass_score_percentage: 70,
    max_attempts: 3,
    time_limit_minutes: null,
    randomize_questions: false,
    randomize_options: false,
    show_results_immediately: true,
    show_correct_answers: true,
    status: 'draft'
  });
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  useEffect(() => {
    fetchAvailableQuestions();
    if (isEditMode) {
      fetchQuestionnaireDetails();
    }
  }, []);

  const fetchAvailableQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableQuestions(response.data.questions || []);
    } catch (error) {
      toast.error('Failed to fetch questions');
    }
  };

  const fetchQuestionnaireDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/questionnaires/${questionnaireId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setFormData({
        title: data.title,
        description: data.description,
        pass_score_percentage: data.pass_score_percentage,
        max_attempts: data.max_attempts,
        time_limit_minutes: data.time_limit_minutes,
        randomize_questions: data.randomize_questions,
        randomize_options: data.randomize_options,
        show_results_immediately: data.show_results_immediately,
        show_correct_answers: data.show_correct_answers,
        status: data.status
      });
      const loadedPurpose = data.purpose || 'conventional';
      setPurpose(loadedPurpose);
      if (loadedPurpose === 'adaptive' && data.sequences && data.sequences.length > 0) {
        setSequences(data.sequences.map(seq => ({
          sequence_number: seq.sequence_number,
          question_ids: seq.questions.map(q => q.question_id),
          collapsed: false
        })));
      } else {
        setSelectedQuestions(data.questions.map(q => q.question_id));
      }
    } catch (error) {
      toast.error('Failed to fetch questionnaire details');
    }
  };

  const handleSubmit = async (status) => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (purpose === 'conventional') {
      if (selectedQuestions.length === 0) {
        toast.error('Please select at least one question');
        return;
      }
    } else {
      // Adaptive: validate sequences have questions
      const hasQuestions = sequences.some(seq => seq.question_ids.length > 0);
      if (!hasQuestions) {
        toast.error('Please add at least one question to a sequence');
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        status,
        purpose,
        question_ids: purpose === 'conventional' ? selectedQuestions : [],
        sequences: purpose === 'adaptive' ? sequences.map(seq => ({
          sequence_number: seq.sequence_number,
          question_ids: seq.question_ids
        })) : []
      };

      if (isEditMode) {
        await axios.put(`${API}/assessments/questionnaires/${questionnaireId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Questionnaire updated successfully!');
      } else {
        await axios.post(`${API}/assessments/questionnaires`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Questionnaire created successfully!');
      }
      navigate('/assessments/manage');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save questionnaire');
    }
    setLoading(false);
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      mcq_single: 'MCQ Single',
      mcq_multiple: 'MCQ Multiple',
      ranking: 'Ranking',
      arrange: 'Arrange',
      drag_drop: 'Drag-Drop',
      matching: 'Matching'
    };
    return labels[type] || type;
  };

  // Sequence helpers for adaptive mode
  const addSequence = () => {
    const nextNum = sequences.length + 1;
    setSequences([...sequences, { sequence_number: nextNum, question_ids: [], collapsed: false }]);
  };

  const removeSequence = (index) => {
    if (sequences.length <= 1) {
      toast.error('At least one sequence is required');
      return;
    }
    const updated = sequences.filter((_, i) => i !== index).map((seq, i) => ({
      ...seq,
      sequence_number: i + 1
    }));
    setSequences(updated);
  };

  const toggleSequenceCollapse = (index) => {
    setSequences(sequences.map((seq, i) =>
      i === index ? { ...seq, collapsed: !seq.collapsed } : seq
    ));
  };

  const toggleSequenceQuestion = (seqIndex, questionId) => {
    setSequences(sequences.map((seq, i) => {
      if (i !== seqIndex) return seq;
      const ids = seq.question_ids.includes(questionId)
        ? seq.question_ids.filter(id => id !== questionId)
        : [...seq.question_ids, questionId];
      return { ...seq, question_ids: ids };
    }));
  };

  const removeSequenceQuestion = (seqIndex, questionId) => {
    setSequences(sequences.map((seq, i) => {
      if (i !== seqIndex) return seq;
      return { ...seq, question_ids: seq.question_ids.filter(id => id !== questionId) };
    }));
  };

  // Get all question IDs already assigned to any sequence (for visual indication)
  const allAssignedQuestionIds = sequences.flatMap(seq => seq.question_ids);

  // Total questions and points for adaptive mode
  const adaptiveTotalQuestions = sequences.reduce((sum, seq) => sum + seq.question_ids.length, 0);
  const adaptiveTotalPoints = sequences.reduce((sum, seq) =>
    sum + seq.question_ids.reduce((s, qId) => {
      const q = availableQuestions.find(q => q.question_id === qId);
      return s + (q?.points || 0);
    }, 0), 0);

  const filteredQuestions = availableQuestions.filter(q =>
    q.question_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center mb-6">
          <Button variant="outline" onClick={() => navigate('/assessments/manage')} className="mr-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Questionnaire' : 'Create New Questionnaire'}
            </h1>
            <p className="text-gray-600 mt-1">Build your assessment by selecting questions and configuring settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6">
              <h2 className="text-[18px] font-bold mb-4">Basic Information</h2>
              
              <div className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    placeholder="e.g., Hand Hygiene Assessment"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Description *</Label>
                  <textarea
                    className="w-full border rounded-md p-3 min-h-[100px]"
                    placeholder="Describe the purpose and coverage of this assessment..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Purpose</Label>
                  <Select value={purpose} onValueChange={(val) => setPurpose(val)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conventional">Conventional Training</SelectItem>
                      <SelectItem value="adaptive">Adaptive Training</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {purpose === 'conventional'
                      ? 'Standard flat question structure'
                      : 'Sequence-based question grouping for adaptive learning'}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-[18px] font-bold mb-4">Assessment Settings</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pass Score (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.pass_score_percentage}
                    onChange={(e) => setFormData({ ...formData, pass_score_percentage: parseFloat(e.target.value) || 70 })}
                  />
                </div>

                <div>
                  <Label>Max Attempts</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_attempts}
                    onChange={(e) => setFormData({ ...formData, max_attempts: parseInt(e.target.value) || 3 })}
                  />
                </div>

                <div className="col-span-2">
                  <Label>Time Limit (minutes) - Optional</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="Leave empty for no time limit"
                    value={formData.time_limit_minutes || ''}
                    onChange={(e) => setFormData({ ...formData, time_limit_minutes: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="randomize_questions"
                    checked={formData.randomize_questions}
                    onCheckedChange={(checked) => setFormData({ ...formData, randomize_questions: checked })}
                  />
                  <Label htmlFor="randomize_questions" className="cursor-pointer">
                    Randomize question order for each attempt
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="randomize_options"
                    checked={formData.randomize_options}
                    onCheckedChange={(checked) => setFormData({ ...formData, randomize_options: checked })}
                  />
                  <Label htmlFor="randomize_options" className="cursor-pointer">
                    Randomize option order (for MCQ questions)
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show_results"
                    checked={formData.show_results_immediately}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_results_immediately: checked })}
                  />
                  <Label htmlFor="show_results" className="cursor-pointer">
                    Show results immediately after submission
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show_answers"
                    checked={formData.show_correct_answers}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_correct_answers: checked })}
                  />
                  <Label htmlFor="show_answers" className="cursor-pointer">
                    Show correct answers in results
                  </Label>
                </div>
              </div>
            </Card>

            {/* Questions Subchapter - Conditional based on purpose */}
            {purpose === 'conventional' ? (
              /* Conventional: Flat question selection */
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-[18px] font-bold">Select Questions ({selectedQuestions.length} selected)</h2>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search questions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {filteredQuestions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {availableQuestions.length === 0 
                      ? 'No questions available. Create questions first in Question Bank.'
                      : 'No questions match your search.'}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredQuestions.map((question) => (
                      <div
                        key={question.question_id}
                        onClick={() => toggleQuestionSelection(question.question_id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedQuestions.includes(question.question_id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            checked={selectedQuestions.includes(question.question_id)}
                            onCheckedChange={() => toggleQuestionSelection(question.question_id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                {getQuestionTypeLabel(question.question_type)}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                                question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {question.difficulty.toUpperCase()}
                              </span>
                              {question.category && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                  {question.category}
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-medium text-gray-900">{question.question_text}</p>
                            <p className="text-xs text-gray-600 mt-1">{question.points} point(s)</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ) : (
              /* Adaptive: Sequence-based question grouping */
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-[18px] font-bold flex items-center gap-2">
                    <Layers className="w-5 h-5 text-blue-600" />
                    Sequences ({sequences.length})
                  </h2>
                  <Button onClick={addSequence} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Sequence
                  </Button>
                </div>

                {/* Search bar for adaptive mode */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {sequences.map((seq, seqIndex) => (
                  <Card key={seqIndex} className="border-2 border-blue-100 overflow-hidden">
                    {/* Sequence Header */}
                    <div
                      className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-50 to-white cursor-pointer"
                      onClick={() => toggleSequenceCollapse(seqIndex)}
                    >
                      <div className="flex items-center gap-3">
                        {seq.collapsed ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        )}
                        <h3 className="font-semibold text-gray-900">Sequence {seq.sequence_number}</h3>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {seq.question_ids.length} question{seq.question_ids.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {sequences.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSequence(seqIndex);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {!seq.collapsed && (
                      <div className="p-5">
                        {/* Selected questions in this sequence */}
                        {seq.question_ids.length > 0 && (
                          <div className="mb-4">
                            <Label className="text-xs uppercase tracking-wide text-gray-500 mb-2 block">
                              Selected Questions
                            </Label>
                            <div className="space-y-2">
                              {seq.question_ids.map((qId, qIdx) => {
                                const question = availableQuestions.find(q => q.question_id === qId);
                                if (!question) return null;
                                return (
                                  <div key={qId} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <span className="text-xs font-medium text-blue-700 shrink-0">Q{qIdx + 1}</span>
                                      <p className="text-sm text-gray-900 truncate">{question.question_text}</p>
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs shrink-0">
                                        {getQuestionTypeLabel(question.question_type)}
                                      </span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeSequenceQuestion(seqIndex, qId)}
                                      className="text-red-500 hover:text-red-700 h-7 w-7 p-0 shrink-0 ml-2"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Available questions to add */}
                        <Label className="text-xs uppercase tracking-wide text-gray-500 mb-2 block">
                          Add Questions to Sequence {seq.sequence_number}
                        </Label>
                        {filteredQuestions.length === 0 ? (
                          <p className="text-sm text-gray-400 py-4 text-center">No questions available</p>
                        ) : (
                          <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                            {filteredQuestions.map((question) => {
                              const isInThisSequence = seq.question_ids.includes(question.question_id);
                              const isInOtherSequence = !isInThisSequence && allAssignedQuestionIds.includes(question.question_id);
                              return (
                                <div
                                  key={question.question_id}
                                  onClick={() => !isInOtherSequence && toggleSequenceQuestion(seqIndex, question.question_id)}
                                  className={`p-3 border rounded-lg transition-all ${
                                    isInThisSequence
                                      ? 'border-blue-500 bg-blue-50 cursor-pointer'
                                      : isInOtherSequence
                                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                  }`}
                                >
                                  <div className="flex items-start space-x-3">
                                    <Checkbox
                                      checked={isInThisSequence}
                                      disabled={isInOtherSequence}
                                      onCheckedChange={() => !isInOtherSequence && toggleSequenceQuestion(seqIndex, question.question_id)}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                          {getQuestionTypeLabel(question.question_type)}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                                          question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {question.difficulty.toUpperCase()}
                                        </span>
                                        {isInOtherSequence && (
                                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                                            In another sequence
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-900 truncate">{question.question_text}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">{question.points} point(s)</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">
              <Card className="p-6">
                <h3 className="font-bold text-lg mb-4">Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Purpose:</span>
                    <span className="font-medium capitalize">{purpose}</span>
                  </div>
                  {purpose === 'adaptive' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sequences:</span>
                      <span className="font-medium">{sequences.length}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Questions:</span>
                    <span className="font-medium">
                      {purpose === 'conventional' ? selectedQuestions.length : adaptiveTotalQuestions}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Points:</span>
                    <span className="font-medium">
                      {purpose === 'conventional'
                        ? selectedQuestions.reduce((sum, qId) => {
                            const q = availableQuestions.find(q => q.question_id === qId);
                            return sum + (q?.points || 0);
                          }, 0)
                        : adaptiveTotalPoints}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pass Score:</span>
                    <span className="font-medium">{formData.pass_score_percentage}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Attempts:</span>
                    <span className="font-medium">{formData.max_attempts}</span>
                  </div>
                  {formData.time_limit_minutes && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time Limit:</span>
                      <span className="font-medium">{formData.time_limit_minutes} min</span>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-lg mb-4">Actions</h3>
                <div className="space-y-3">
                  <Button
                    onClick={() => handleSubmit('draft')}
                    variant="outline"
                    className="w-full"
                    disabled={loading}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save as Draft
                  </Button>
                  <Button
                    onClick={() => handleSubmit('published')}
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={loading}
                  >
                    Publish Questionnaire
                  </Button>
                  <Button
                    onClick={() => navigate('/assessments/manage')}
                    variant="outline"
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </Card>

              <Card className="p-4 bg-blue-50 border-blue-200">
                <p className="text-xs text-blue-800">
                  <span className="font-bold">💡 Tip:</span>{' '}
                  {purpose === 'conventional'
                    ? 'You can save as draft and publish later. Published questionnaires can be tagged to training chapters.'
                    : 'Adaptive training uses sequences to structure questions into progressive stages. Questions within a sequence are grouped and evaluated together.'}
                </p>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateQuestionnairePage;
